import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { ITeamMemberDTO } from '../../types/Parties';
import { ICoSettlementAgentObj } from '../../types/SettlementAgent';
import EnqueueSnackbarAction from '../components/EnqueueSnackbarAction/EnqueueSnackbarAction';
import { DocGenerationSource } from '../constants/docGenerationSource';
import { OrderAction } from '../constants/orderActions';
import { useAppDispatch } from '../redux-store/hooks';
import {
  getSsoTokenAsync,
  setAlert,
} from '../redux-store/ipenNotary/ipenNotarySlice';
import { ISsoTokenRequest } from '../redux-store/ipenNotary/ipenNotaryTypes';
import { setActiveSignerIds } from '../redux-store/ipenSession/ipenSessionSlice';
import { enqueueSnackbar } from '../redux-store/notifications/notificationsSlice';
import {
  addCoSettlementAgentsToOrderAsync,
  cancelOrderAsync,
  unlockPartyAsync,
  unlockSessionAsync,
  updateOrderActionsAsync,
  updateOrderDetailsSignerArray,
} from '../redux-store/orders/ordersSlice';
import { UnlockSessionResponse } from '../redux-store/orders/viewOrdersAPI.types';
import {
  formatPartyName,
  getUpdatedSignerEvents,
  setUpdatedSignerEvents,
} from '../utils/helpers';

const useOrderActions = (order: any, currentUserName?: string) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const showSnackbar = (message: string) => {
    dispatch(
      enqueueSnackbar({
        message,
        key: new Date().getTime() + Math.random(),
        options: {
          variant: 'error',
          persist: true,
          action: (key: number) => <EnqueueSnackbarAction snackbarId={key} />,
        },
      })
    );
  };

  const setActiveSigners = (activeParties: UnlockSessionResponse) => {
    const activePartyIds = Object.entries(activeParties)
      .filter(([, isLocked]) => !isLocked)
      .map(([id]) => id);
    dispatch(setActiveSignerIds(activePartyIds));
  };

  const actions = useMemo(
    () => ({
      async getToken(partyLink: string, orderId?: string) {
        const id: string = order?.Id || orderId;
        if (id) {
          const partyLinkSplit = partyLink.split('/');
          const ticketId = partyLinkSplit[partyLinkSplit.length - 1];
          const payload: ISsoTokenRequest = {
            TicketId: ticketId,
            OrderId: id,
          };

          return await dispatch(getSsoTokenAsync(payload));
        }
        return false;
      },

      async goResetLogin(partyId: string) {
        const payload = {
          OrderId: order?.Id ? order.Id.toString() : undefined,
          PartyId: partyId.toString(),
        };
        const signerIndex = order?.OrderDetails.Signers.findIndex(
          (e: any) => e?.Id == partyId
        ); // find the index in which the signer is
        if (payload.OrderId && payload.PartyId) {
          await dispatch(unlockPartyAsync(payload))
            .then((response: any) => {
              if (response && response.error) {
                showSnackbar(`An error occurred resetting the login for
                ${formatPartyName(
                  {
                    FirstName:
                      order?.OrderDetails.Signers[signerIndex].FirstName,
                    LastName: order?.OrderDetails.Signers[signerIndex].LastName,
                    MiddleName:
                      order?.OrderDetails.Signers[signerIndex].MiddleName,
                    Suffix: order?.OrderDetails.Signers[signerIndex].Suffix,
                  },
                  'signer'
                )}.`);
              } else {
                const authenticatedEvents = getUpdatedSignerEvents(
                  partyId,
                  order
                ); // get a new object of events for the signer
                const updatedSigner = setUpdatedSignerEvents(
                  partyId,
                  authenticatedEvents,
                  order
                ); // get a new objects of signer and the new event applied
                const all = order?.OrderDetails.Signers.slice(); // get array copy of all signers
                all[signerIndex] = updatedSigner; // assign the updated signer to the collection of signers
                dispatch(updateOrderDetailsSignerArray(all[signerIndex])); // set it in the store
              }
            })
            .then(() => {
              dispatch(
                setAlert({
                  type: 'success',
                  text: 'Successfully Reset Party Login',
                })
              );
            });
        } else {
          showSnackbar('There was an issue executing this request');
        }
      },

      async goUnlockSession(partyIds: string | string[] | number | number[]) {
        const errorMessage =
          'There was an issue starting the IPEN session. Please try again.';
        const partyIdStrings = Array.isArray(partyIds)
          ? partyIds.map((id) => id.toString())
          : [partyIds.toString()];

        if (!order.Id || !partyIdStrings.length) {
          showSnackbar(errorMessage);
          return;
        }

        const response = await dispatch(
          unlockSessionAsync({
            OrderId: order.Id.toString(),
            PartyIds: partyIdStrings,
          })
        );

        if (response.meta.requestStatus === 'fulfilled') {
          setActiveSigners(
            (response.payload as { activeParties: UnlockSessionResponse })
              .activeParties
          );
        } else {
          showSnackbar(errorMessage);
        }
      },

      async handleAddCosettlementAgents(
        data: ICoSettlementAgentObj[],
        docGen: DocGenerationSource
      ) {
        const solexSignerIds: number[] = [];
        const signerIds: number[] = order?.OrderDetails?.Signers?.map(
          (s: any) => s.SolexSigner.SolexId
        );
        if (signerIds.length) solexSignerIds.push(Math.max(...signerIds));

        const escrowIds: number[] = order?.OrderDetails.EscrowTeam?.map(
          (s: any) => s.SolexSigner.SolexId
        );
        if (escrowIds.length) solexSignerIds.push(Math.max(...escrowIds));

        const settlementIds: number[] = order?.OrderDetails.SettlementTeam?.map(
          (s: any) => s.SolexSigner.SolexId
        );
        if (settlementIds.length)
          solexSignerIds.push(Math.max(...settlementIds));

        const notaryId: number =
          order?.OrderDetails?.IPEN?.Notary?.SolexSigner.SolexId;
        if (notaryId) solexSignerIds.push(notaryId);
        const maxnumber: number = Math.max(...solexSignerIds) + 1;
        const dataToSend: ITeamMemberDTO[] = [];

        data.map((sa) => {
          dataToSend.push({
            Id: maxnumber.toString(),
            FirstName: sa.FirstName,
            LastName: sa.LastName,
            Email: sa.Email,
            PhoneNumber: sa.PhoneNumber,
          });
        });

        await dispatch(
          addCoSettlementAgentsToOrderAsync({
            OrderId: order.Id,
            TeamMembersListToAdd: dataToSend,
          })
        ).then((response: any) => {
          if (response && response.error) {
            showSnackbar('An error occurred adding co-settlement agents.');
          } else {
            dispatch(
              setAlert({
                type: 'success',
                text: 'Successfully Added Co Settlement Agents',
              })
            );
            dispatch(
              updateOrderActionsAsync({
                OrderId: order.Id,
                ChecklistId:
                  docGen === DocGenerationSource.CX
                    ? OrderAction.PreClosing_SettlementAgent_AddedCoSettlementAgent
                    : OrderAction.PreClosing_EscrowOfficer_AddedEscrowAssistant,
                Complete: true,
                Signer: currentUserName,
              })
            );
          }
        });
      },

      async handleUpdateOrderActions(checklistId: string, complete: boolean) {
        dispatch(
          updateOrderActionsAsync({
            OrderId: order.Id,
            ChecklistId: checklistId,
            Complete: complete,
            Signer: complete ? currentUserName : '',
          })
        );
      },

      async handleCancelConfirmActions(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        action?: string
      ) {
        if (action === 'primary') {
          const { id } = params;
          if (id) {
            dispatch(cancelOrderAsync({ orderId: id })).then(() => {
              dispatch(
                updateOrderActionsAsync({
                  OrderId: order.Id,
                  ChecklistId:
                    OrderAction.AnyTime_SettlementAgent_CanceledOrder,
                  Complete: true,
                  Signer: currentUserName,
                })
              );
            });
          }
        }
      },
    }),
    [dispatch]
  );

  return actions;
};

export default useOrderActions;
